import { useEffect, useRef, useState } from "react";

import {
  DOCUMENT_SIGNING_STAGES,
  DOCUMENT_SIGNING_STAGES_TYPE,
  SIGNING_STATUSES,
} from "../../constants";
import { BatchedDocumentsItemMap } from "../../types";

import { setShowWarningDialogItemToLocalStorage } from "../../../auth-provider/auto-logout/utils";
import { getBatchCcb } from "../../utils/get-batch-ccb";
import { localSigningDocuments } from "../../utils/local-signing-documents";
import { checkSignatures } from "../../utils/check-signature";
import { checkDocumentsStatuses } from "../../utils/check-documents-statuses";
import { getIsTaskFinished } from "../../utils/get-is-task-finished";

import { UseSigningOptions } from "./types";

export const useSigning = ({
  status,
  isSigning,
  certificate,
  taskId,
  updateDocsToView,
  hasToStopProcess,
  isOpened,
  error,
  signingRequestIntervalInMilliseconds,
  documentsInBatch,
  unsignedDocuments,
  documentsToView,
  setError,
  setStatus,
  setDocumentsToView,
}: UseSigningOptions) => {
  // isOpenedRef нужен, чтобы остановить опрос статуса задания
  // при закрытии массового во время подписания
  const isOpenedRef = useRef(false);

  const [isLastStepBeforeFinish, setIsLastStepBeforeFinish] = useState(false);
  // документы, находящиеся в процессе подписания
  const [batch, setBatch] = useState<BatchedDocumentsItemMap>({});
  // этап подписания, на котором находится текущая пачка документов
  const [batchSignStage, setBatchSignStage] =
    useState<DOCUMENT_SIGNING_STAGES_TYPE>(DOCUMENT_SIGNING_STAGES.EMPTY);

  useEffect(() => {
    isOpenedRef.current = isOpened;

    if (!isOpened) {
      setIsLastStepBeforeFinish(false);
      setBatch({});
      setBatchSignStage(DOCUMENT_SIGNING_STAGES.EMPTY);
    }
  }, [isOpened]);

  // 4 этап
  // этап проверки документов после отправки на подписание на бэк
  useEffect(() => {
    if (
      status !== SIGNING_STATUSES.IS_SIGNING ||
      batchSignStage !== DOCUMENT_SIGNING_STAGES.SIGNATURE_IS_CHECKING
    ) {
      return;
    }

    checkDocumentsStatuses(
      taskId,
      isLastStepBeforeFinish,
      documentsToView,
      setBatch,
      setStatus,
      setBatchSignStage,
      setDocumentsToView,
      setError
    );
  }, [
    taskId,
    documentsToView,
    certificate,
    batchSignStage,
    isLastStepBeforeFinish,
    status,
  ]);

  // 3 этап
  // отправка пачки на провреку подписи
  useEffect(() => {
    const sentForSignBatch = async () => {
      if (batchSignStage === DOCUMENT_SIGNING_STAGES.SIGNED_LOCALLY) {
        const sentForSign = await checkSignatures(taskId, isSigning, batch);

        if (sentForSign) {
          updateDocsToView(sentForSign);
        }

        // даже если в текущей пачке будет нечего обработать, нужно перейти на следующий этап
        // чтобы подписание не зависло и были обработаны следующие документы
        setBatchSignStage(DOCUMENT_SIGNING_STAGES.SIGNATURE_IS_CHECKING);
      }
    };

    sentForSignBatch();
  }, [certificate, batch, batchSignStage, taskId]);

  // 2 этап
  // локальная подпись пачки документов
  useEffect(() => {
    const localSignedBatch = async () => {
      if (batchSignStage === DOCUMENT_SIGNING_STAGES.GOT_CCB) {
        const localSigned = await localSigningDocuments(
          setError,
          certificate?.serial,
          batch
        );

        if (localSigned) {
          setBatch(localSigned);
          updateDocsToView(localSigned);
          setBatchSignStage(DOCUMENT_SIGNING_STAGES.SIGNED_LOCALLY);
        }
      }
    };

    localSignedBatch();
  }, [certificate, batch, batchSignStage]);

  // 1 этап
  // если начато подписание, то получаем ккб для документов
  // если ккб не было получено - проверяем статус задания на бэке для завершения задания
  // если получили ккб, то переходим к локальному подписанию, изменив batchSignStage
  useEffect(() => {
    if (
      error ||
      hasToStopProcess ||
      !isOpened ||
      status === SIGNING_STATUSES.SIGNING_COMPLETED
    ) {
      return;
    }

    if (
      status === SIGNING_STATUSES.IS_SIGNING &&
      batchSignStage === DOCUMENT_SIGNING_STAGES.EMPTY
    ) {
      const signingProcess = async () => {
        if (status !== SIGNING_STATUSES.IS_SIGNING || !isOpenedRef.current) {
          return;
        }

        const ccbBatch = await getBatchCcb(
          documentsInBatch,
          taskId,
          unsignedDocuments
        ).catch((e) => null);

        // если не получили ккб - проверяем статус задания
        if (!ccbBatch || !Object.values(ccbBatch).length) {
          const isTaskFinished = await getIsTaskFinished(taskId, setError);

          if (isTaskFinished) {
            // переводим подписание сразу на  проверку статусов, так как новых документов не получили
            // но могли остаться неподтвержденные документы с предыдущих итерациий
            setIsLastStepBeforeFinish(true);
            setBatchSignStage(DOCUMENT_SIGNING_STAGES.SIGNATURE_IS_CHECKING);
          } else {
            await new Promise((resolve) =>
              setTimeout(
                () => resolve(signingProcess()),
                signingRequestIntervalInMilliseconds
              )
            );

            return;
          }
        } else {
          setBatch(ccbBatch);
          updateDocsToView(ccbBatch);
          setBatchSignStage(DOCUMENT_SIGNING_STAGES.GOT_CCB);
        }
      };

      // подписание большого количества документов может занять более 20 минут,
      // обновляем время до предупреждения и разлогина пока идет подписание
      setShowWarningDialogItemToLocalStorage();
      signingProcess();
    }
  }, [
    status,
    isSigning,
    batchSignStage,
    setError,
    certificate,
    taskId,
    updateDocsToView,
    hasToStopProcess,
    isOpened,
    error,
    signingRequestIntervalInMilliseconds,
    documentsInBatch,
    unsignedDocuments,
  ]);
};
